import { AddIcon } from '@chakra-ui/icons';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Skeleton,
  Stack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  UserRole,
  useGetCompanyDocumentsQuery,
  useGetCompanyQuery,
} from '@blockpulse3/graphql/hooks';
import {
  AddIdentityDocuments,
  ErrorQueryCard,
  LinkButton,
  useGetCompanyDocuments,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { DocumentTable } from '../DocumentTable';

type Props = {
  backHref?: string;
};

/**
 * DocumentsView.
 *
 * @returns {JSX.Element}
 */
export function DocumentsView({ backHref }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nDocuments = useTranslations('DocumentsView');

  const [isAddingDocuments, setIsAddingDocuments] = useState<boolean>(false);

  const { user } = useUser();

  const { companyId = '' } = useParams();

  const { data: companyData, loading: loadingCompany } = useGetCompanyQuery({
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  const {
    data,
    loading: loadingDocuments,
    error,
    refetch,
  } = useGetCompanyDocumentsQuery({
    variables: {
      companyId,
    },
    skip: !companyId,
  });

  const [getCompanyDocuments] = useGetCompanyDocuments();

  const identityId = companyData?.company.identity?.id;

  if (loadingCompany || loadingDocuments) {
    return (
      <Stack spacing="6">
        {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
        <Heading>{i18nDocuments('Title')}</Heading>
        <Skeleton>
          <Card h="300px" />
        </Skeleton>
      </Stack>
    );
  }

  if (error || !identityId) {
    return (
      <Stack spacing="6">
        {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
        <Heading>{i18nDocuments('Title')}</Heading>
        <ErrorQueryCard h="300px" />
      </Stack>
    );
  }

  const documents = data?.getCompanyDocuments.map(({ document }) => document) || [];
  const hasAllDocuments = documents.length >= getCompanyDocuments().length;
  const isUploadAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );

  return (
    <Stack spacing="6">
      {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
      <Heading>{i18nDocuments('Title')}</Heading>
      <Card>
        <CardHeader alignItems="center" as={HStack} justifyContent="space-between">
          <Heading size="md">{i18nDocuments('CardTitleCompany')}</Heading>
          {!hasAllDocuments && isUploadAuthorized && (
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              variant="secondary"
              w="fit-content"
              onClick={(): void => setIsAddingDocuments((val) => !val)}
            >
              {i18nGeneral('AddDocument')}
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {isAddingDocuments && (
            <Stack pb="4">
              <AddIdentityDocuments
                identityId={identityId}
                onlyIdentityDocuments={true}
                onUploadCompleted={refetch}
              />
            </Stack>
          )}
          <DocumentTable identityId={identityId} values={documents} onDeleteCompleted={refetch} />
        </CardBody>
      </Card>
    </Stack>
  );
}

export type DocumentsViewProps = Props;
